'use client';

import { useEffect } from 'react';
import { countUncaughtError, logUncaughtError } from '@/app/common/components/ErrorBoundary/utils.ts';

// What a browser reports instead of a cross-origin script's error, with no file, line, stack or message behind it.
const OPAQUE_CROSS_ORIGIN_ERROR = /^Script error\.?$/;

// Our bundle only: an inline script reports the document URL, and so does every advert tag GTM injects inline.
const isOwnCode = (filename: string) => filename.includes('/_next/');

// Error boundaries only see render and commit. A throw in an event handler or an async callback leaves
// the page looking healthy with a dead control and no signal, so report those here instead.
const report = (id: string, error: unknown, source?: string) => {
  try {
    logUncaughtError(id, error instanceof Error ? error : new Error(String(error)), source);
  } catch {
    // reporting must never become the failure it is reporting
  }
};

const GlobalErrorReporter = () => {
  useEffect(() => {
    // Each branch that is not ours is counted rather than logged: it repeats every page view and we cannot fix it.
    const onError = (event: Event) => {
      // A resource that failed to load dispatches a bare Event here, carrying no error at all.
      if (!(event instanceof ErrorEvent)) return;

      if (OPAQUE_CROSS_ORIGIN_ERROR.test(event.message)) {
        countUncaughtError('opaque');
        return;
      }

      if (!isOwnCode(event.filename)) {
        countUncaughtError('third-party');
        return;
      }

      report('error', event.error ?? event.message, `at:${event.filename}:${event.lineno}:${event.colno}`);
    };
    const onRejection = (event: PromiseRejectionEvent) => report('unhandledrejection', event.reason);

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
};

export default GlobalErrorReporter;
